// src/pages/GuideDetailPage.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { useAuth } from '../contexts/AuthContext';
import { endpoints, API_BASE_URL, CHROME_STORE_URL } from '../services/api';
import { detectExtension, playWithExtension, getDomain, highlightRect } from '../services/extension';
import ShareModal from '../components/ShareModal/ShareModal';
import '../styles/pages/GuideDetailPage.css';
import { getFriendlyError } from '../components/errorUtils';

const ZOOM_STEPS = [1, 1.5, 2, 3, 4];

// Textarea that grows with its content — no scrollbars mid-edit.
const Grow = ({ value, ...rest }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    // scrollHeight is content-box; add the borders back or border-box clips it
    // into a scrollbar.
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  }, [value]);
  return <textarea ref={ref} rows="1" value={value} {...rest} />;
};

// ── Step viewer — screenshots + highlight + zoom ─────────────────────────────
const StepViewer = ({ steps, token, editing, onStepChange }) => {
  const [active, setActive]   = useState(0);
  const [zoom,   setZoom]     = useState(1);
  const [natural, setNatural] = useState(null);
  const [imgError, setImgError] = useState(false);
  const stripRef = useRef(null);

  const step  = steps[active];
  const total = steps.length;

  const src = useMemo(() => {
    const p = step?.screenshot_path;
    if (!p) return null;
    // Relative path = the authenticated proxy route; <img> can't send headers.
    return p.startsWith('/api/') ? `${API_BASE_URL}${p}?token=${encodeURIComponent(token)}` : p;
  }, [step, token]);

  const rect = highlightRect(step, natural);

  // Zoom origin = centre of the highlighted element, so zooming in lands on it.
  const origin = rect
    ? `${rect.left + rect.width / 2}% ${rect.top + rect.height / 2}%`
    : '50% 50%';

  const go = useCallback((i) => {
    setActive(Math.max(0, Math.min(total - 1, i)));
    setZoom(1);
    setNatural(null);
    setImgError(false);
  }, [total]);

  const bumpZoom = useCallback((dir) => {
    setZoom(z => {
      const i = ZOOM_STEPS.indexOf(z);
      const next = i === -1 ? 1 : i + dir;
      return ZOOM_STEPS[Math.max(0, Math.min(ZOOM_STEPS.length - 1, next))];
    });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.matches('input, textarea')) return;
      if (e.key === 'ArrowRight') go(active + 1);
      else if (e.key === 'ArrowLeft') go(active - 1);
      else if (e.key === '+' || e.key === '=') bumpZoom(1);
      else if (e.key === '-') bumpZoom(-1);
      else if (e.key === '0') setZoom(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, go, bumpZoom]);

  // Keep the active step in view. Scrolls the list itself — scrollIntoView
  // would drag the whole page down to the viewer on first render.
  useEffect(() => {
    const list = stripRef.current;
    const item = list?.children[active];
    if (!list || !item || typeof list.scrollTo !== 'function') return;
    list.scrollTo({
      top: item.offsetTop - list.clientHeight / 2 + item.clientHeight / 2,
      behavior: 'smooth',
    });
  }, [active]);

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <section className="gdp__viewer">
      <div className="gdp__stage-col">
        {/* Stage */}
        <div className="gdp__stage m-sheet">
          <div className="gdp__stage-bar">
            <span className="m-eyebrow">Step <b className="m-mono">{pad(active + 1)}</b> / {pad(total)}</span>
            <div className="gdp__zoom">
              <button onClick={() => bumpZoom(-1)} disabled={zoom === ZOOM_STEPS[0]} title="Zoom out (-)" aria-label="Zoom out">−</button>
              <span className="gdp__zoom-level m-mono">{Math.round(zoom * 100)}%</span>
              <button onClick={() => bumpZoom(1)} disabled={zoom === ZOOM_STEPS[ZOOM_STEPS.length - 1]} title="Zoom in (+)" aria-label="Zoom in">+</button>
              {zoom !== 1 && <button className="gdp__zoom-reset" onClick={() => setZoom(1)} title="Reset (0)">Fit</button>}
            </div>
          </div>

          <div className="gdp__stage-body">
            {src && !imgError ? (
              <div
                className="gdp__frame"
                style={{ transform: `scale(${zoom})`, transformOrigin: origin }}
                onClick={() => setZoom(z => (z === 1 ? 2.5 : 1))}
                role="presentation"
              >
                <img
                  src={src}
                  alt={`Step ${active + 1}`}
                  className="gdp__shot"
                  onLoad={e => setNatural({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
                  onError={() => setImgError(true)}
                />
                {rect && (
                  <span
                    className="gdp__highlight"
                    style={{
                      left: `${rect.left}%`, top: `${rect.top}%`,
                      width: `${rect.width}%`, height: `${rect.height}%`,
                      // Ring stays hairline-thin as the image scales up
                      outlineWidth: `${Math.max(1, 2 / zoom)}px`,
                    }}
                  />
                )}
              </div>
            ) : (
              <div className="gdp__stage-empty">No screenshot for this step</div>
            )}

            {/* Arrows */}
            <button className="gdp__arrow gdp__arrow--prev" onClick={() => go(active - 1)} disabled={active === 0} aria-label="Previous step">‹</button>
            <button className="gdp__arrow gdp__arrow--next" onClick={() => go(active + 1)} disabled={active === total - 1} aria-label="Next step">›</button>
          </div>
        </div>

        {/* Progress: same dot-track language as the playback panel */}
        <div className="gdp__progress" aria-hidden="true">
          <div className="gdp__progress-fill" style={{ width: `${((active + 1) / total) * 100}%` }} />
        </div>
        <p className="gdp__keys m-eyebrow">← → to move · + − to zoom</p>
      </div>

      {/* Ruled step list — the current step carries the ring */}
      <div className="gdp__steps m-sheet">
        <div className="gdp__steps-head">
          <span className="m-eyebrow">Steps</span>
          <span className="m-eyebrow m-mono">{pad(total)}</span>
        </div>
        <ol className="gdp__list" ref={stripRef}>
          {steps.map((s, i) => {
            const cur = i === active;
            return (
              <li key={i} className={`gdp__item${cur ? ' gdp__item--cur' : ''}`}>
                {cur && editing ? (
                  <div className="gdp__item-row">
                    <span className="gdp__item-num m-mono">{pad(i + 1)}</span>
                    <Grow
                      className="gdp__instruction-input"
                      value={s.instruction || ''}
                      onChange={(e) => onStepChange(i, e.target.value)}
                      placeholder={`Step ${i + 1}`}
                      aria-label={`Step ${i + 1} instruction`}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    className="gdp__item-row"
                    onClick={() => go(i)}
                    aria-current={cur ? 'step' : undefined}
                  >
                    <span className="gdp__item-num m-mono">{pad(i + 1)}</span>
                    <span className="gdp__item-text">{s.instruction || `Step ${i + 1}`}</span>
                  </button>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────
const GuideDetailPage = () => {
  const { guideId }  = useParams();
  const { token }    = useAuth();
  const navigate     = useNavigate();
  const location     = useLocation();

  const [guide,        setGuide]        = useState(location.state?.guide || null);
  const [playData,     setPlayData]     = useState(null);
  const [loading,      setLoading]      = useState(!location.state?.guide);
  const [error,        setError]        = useState('');
  const [copied,       setCopied]       = useState(false);
  const [playCount,    setPlayCount]    = useState(null);
  const [confirmDel,   setConfirmDel]   = useState(false);
  const [sharingGuide, setSharingGuide] = useState(null);
  const [editing,   setEditing]   = useState(false);
  const [draft,     setDraft]     = useState(null);   // { name, shortcut, description }
  const [stepText,  setStepText]  = useState({});     // step_number -> edited instruction
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState('');
  const [pdfLoading,          setPdfLoading]          = useState(false);
  const [pdfPrivacyLoading,   setPdfPrivacyLoading]   = useState(false);
  const [videoLoading,        setVideoLoading]        = useState(false);
  const [videoError,          setVideoError]          = useState('');
  const [videoPrivacyLoading, setVideoPrivacyLoading] = useState(false);
  const [userPlan,            setUserPlan]            = useState(null);
  const [hasExtension, setHasExtension] = useState(null); // null = still checking
  const [playState,    setPlayState]    = useState('idle');
  const [playError,    setPlayError]    = useState('');

  // ── Fetch guide — skip if we already have it from drawer state ─────────────
  useEffect(() => {
    if (location.state?.guide) return; // already have data
    if (!guideId) return;

    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    fetch(endpoints.guides.detail(guideId), { headers })
      .then(r => { if (!r.ok) throw new Error('Guide not found'); return r.json(); })
      .then(data => { setGuide(data); setLoading(false); })
      .catch(err => { setError(getFriendlyError(err.message, 'Guide Detail')); setLoading(false); });
  }, [guideId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch play payload — screenshot URLs + the steps the extension replays ─
  useEffect(() => {
    if (!token || !guideId) return;
    fetch(endpoints.guides.play(guideId), { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setPlayData(data); })
      .catch(() => {});
  }, [guideId, token]);

  // ── Extension detection ────────────────────────────────────────────────────
  useEffect(() => { detectExtension().then(setHasExtension); }, []);

  useEffect(() => {
    const onStatus = (e) => {
      const { status, error: err } = e.detail || {};
      if (status === 'navigating')    setPlayState('launching');
      if (status === 'waiting_login') setPlayState('waiting_login');
      if (status === 'started')       setPlayState('playing');
      if (status === 'error') {
        setPlayState('error');
        setPlayError(err || 'Something went wrong. Please try again.');
      }
    };
    window.addEventListener('michii:play_status', onStatus);
    return () => window.removeEventListener('michii:play_status', onStatus);
  }, []);

  // ── Fetch user plan ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    fetch(endpoints.auth.me, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setUserPlan(data.plan); })
      .catch(() => {});
  }, [token]);

  // ── Fetch play count (owner only) ──────────────────────────────────────────
  useEffect(() => {
    if (!token || !guide?.is_owner) return;
    fetch(endpoints.analytics.guide(guideId), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setPlayCount(data.total_plays ?? 0); })
      .catch(() => {});
  }, [guideId, token, guide?.is_owner]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleCopyShortcut = async () => {
    if (!guide?.shortcut) return;
    try {
      await navigator.clipboard.writeText(guide.shortcut);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) {}
  };

  // Play right here: no /play page hop. No extension → the Chrome Web Store.
  // Always re-detects: the answer changes the moment the user installs.
  const handlePlay = async ({ openStore = true } = {}) => {
    const installed = await detectExtension();
    setHasExtension(installed);

    if (installed) {
      setPlayError('');
      setPlayState('launching');
      playWithExtension(playData || guide);
      return true;
    }
    if (openStore) {
      ReactGA.event('chrome_store_click', { click_source: 'guide_detail_play' });
      window.open(CHROME_STORE_URL, '_blank', 'noopener');
    }
    setPlayState('needs_extension');
    return false;
  };

  // A freshly installed extension has no content script in this already-open
  // tab, so a reload is the only way to reach it.
  const recheckExtension = async () => {
    if (!(await handlePlay({ openStore: false }))) window.location.reload();
  };

  const handleDelete = async () => {
    setConfirmDel(false);
    try {
      const res = await fetch(endpoints.guides.detail(guideId), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete');
      navigate('/my-guides');
    } catch (err) {
      setError(getFriendlyError(err.message,'Guide Detail'));
    }
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await fetch(endpoints.guides.exportPdf(guideId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to download PDF');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      const safe = (guide.name || 'guide').replace(/\s+/g, '_').replace(/[^A-Za-z0-9._-]/g, '');
      a.download = `${safe || 'guide'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { setError(getFriendlyError(err.message, 'Guide Details')); }
    finally { setPdfLoading(false); }
  };

  const handleDownloadPdfPrivacy = async () => {
    setPdfPrivacyLoading(true);
    try {
      const res = await fetch(`${endpoints.guides.exportPdf(guideId)}?privacy=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to download PDF');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      const safe = (guide.name || 'guide').replace(/\s+/g, '_').replace(/[^A-Za-z0-9._-]/g, '');
      a.download = `${safe || 'guide'}_private.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { setError(getFriendlyError(err.message, 'Guide Details')); }
    finally { setPdfPrivacyLoading(false); }
  };

  const handleDownloadVideo = async () => {
    setVideoLoading(true);
    setVideoError('');
    try {
      const res = await fetch(endpoints.guides.exportVideo(guideId), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Video export failed.');
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      const safe = (guide.name || 'guide').replace(/\s+/g, '_').replace(/[^A-Za-z0-9._-]/g, '');
      a.download = `${safe || 'guide'}.mp4`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setVideoError(err.message);
      setTimeout(() => setVideoError(''), 4000);
    } finally {
      setVideoLoading(false);
    }
  };

  const handleDownloadVideoPrivacy = async () => {
    setVideoPrivacyLoading(true);
    setVideoError('');
    try {
      const res = await fetch(`${endpoints.guides.exportVideo(guideId)}?privacy=true`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Video export failed.');
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      const safe = (guide.name || 'guide').replace(/\s+/g, '_').replace(/[^A-Za-z0-9._-]/g, '');
      a.download = `${safe || 'guide'}_private.mp4`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setVideoError(err.message);
      setTimeout(() => setVideoError(''), 4000);
    } finally {
      setVideoPrivacyLoading(false);
    }
  };

  const handleGuideUpdate = useCallback((updated) => {
    setGuide(prev => ({ ...prev, ...updated }));
  }, []);

  // Screenshots + bbox come from the play payload; fall back to the detail
  // steps (instructions only) while it loads or if the user lacks play access.
  const viewerSteps = useMemo(() => {
    const base = playData?.steps?.length ? playData.steps : (guide?.steps || []);
    return base.map((s, i) => {
      const g = guide?.steps?.[i] || {};
      const num = g.step_number ?? s.step_number ?? i + 1;
      return { ...g, ...s, step_number: num, instruction: stepText[num] ?? g.instruction ?? s.instruction };
    });
  }, [playData, guide, stepText]);

  // ── Inline editing ─────────────────────────────────────────────────────────
  const startEdit = useCallback(() => {
    setDraft({
      name: guide?.name || '',
      shortcut: (guide?.shortcut || '').replace(/^\//, ''),
      description: guide?.description || '',
    });
    setStepText({});
    setSaveError('');
    setEditing(true);
  }, [guide]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setDraft(null);
    setStepText({});
    setSaveError('');
  }, []);

  const editStep = useCallback((i, text) => {
    setStepText(prev => ({ ...prev, [viewerSteps[i]?.step_number ?? i + 1]: text }));
  }, [viewerSteps]);

  const metaDirty = !!draft && (
    draft.name !== (guide?.name || '') ||
    draft.shortcut !== (guide?.shortcut || '').replace(/^\//, '') ||
    draft.description !== (guide?.description || '')
  );
  const dirtySteps = (guide?.steps || [])
    .map((s, i) => ({ step_number: s.step_number ?? i + 1, was: s.instruction }))
    .filter(s => stepText[s.step_number] !== undefined && stepText[s.step_number] !== s.was)
    .map(s => ({ step_number: s.step_number, instruction: stepText[s.step_number] }));
  const dirty = metaDirty || dirtySteps.length > 0;

  const saveEdit = useCallback(async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setSaveError('');
    const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
    try {
      let updated = null;

      if (metaDirty) {
        const res = await fetch(endpoints.guides.update(guideId), {
          method: 'PUT', headers, body: JSON.stringify(draft),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(
            res.status === 400 && String(data.detail || '').toLowerCase().includes('shortcut')
              ? 'This shortcut is already in use. Please choose a different one.'
              : data.detail || 'Failed to update guide'
          );
        }
        updated = data;
      }

      if (dirtySteps.length) {
        const res = await fetch(endpoints.guides.steps(guideId), {
          method: 'PATCH', headers, body: JSON.stringify(dirtySteps),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Failed to update steps');
        updated = data;
      }

      if (updated) setGuide(prev => ({ ...prev, ...updated }));
      cancelEdit();
    } catch (err) {
      setSaveError(getFriendlyError(err.message, 'Edit Guide'));
    } finally {
      setSaving(false);
    }
  }, [dirty, saving, metaDirty, dirtySteps, draft, guideId, token, cancelEdit]);

  // ?edit=1 (e.g. the Edit button on the guides list) opens straight in edit mode.
  const autoEdited = useRef(false);
  useEffect(() => {
    if (autoEdited.current || !guide?.is_owner) return;
    if (new URLSearchParams(location.search).get('edit') !== '1') return;
    autoEdited.current = true;
    startEdit();
    navigate(location.pathname, { replace: true, state: location.state });
  }, [guide, location, navigate, startEdit]);

  // Esc cancels, Cmd/Ctrl+Enter saves.
  useEffect(() => {
    if (!editing) return;
    const onKey = (e) => {
      if (e.key === 'Escape') cancelEdit();
      else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveEdit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, cancelEdit, saveEdit]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="gdp">
      <div className="gdp__inner">
        <div className="skeleton" style={{ height: 16, width: 60, borderRadius: 6, marginBottom: 24 }} />
        <div className="gdp__content" style={{ gap: 12 }}>
          <div className="skeleton" style={{ height: 32, width: '50%', borderRadius: 8 }} />
          <div className="skeleton" style={{ height: 16, width: '25%', borderRadius: 6 }} />
          <div className="skeleton" style={{ height: 14, width: '80%', borderRadius: 6 }} />
          <div className="skeleton" style={{ height: 46, width: 180, borderRadius: 12, marginTop: 8 }} />
        </div>
        <div className="skeleton gdp__hero" style={{ marginTop: 24 }} />
      </div>
    </div>
  );

  if (error) return (
    <div className="gdp">
    <div className="gdp__inner gdp__inner--error">
      <p className="page-error">{error}</p>
      {!token && (
        <p className="gdp__error-note">
          This guide may be private.{' '}
          <a href={`/login?redirect=${encodeURIComponent(window.location.pathname)}`}>
            Log in
          </a>{' '}
          to access guides shared with you.
        </p>
      )}
      <button className="gdp__back-btn" onClick={() => navigate(-1)}>← Go back</button>
    </div>
  </div>
  );

  if (!guide) return null;

  const stepCount = guide.steps?.length ?? 0;
  const isOwner   = guide.is_owner;
  const startUrl  = playData?.start_url || guide.start_url;

  return (
    <div className="gdp">
      <div className="gdp__inner">

        {/* ── Back ─────────────────────────────────────────────────── */}
        <button className="gdp__back-btn" onClick={() => navigate(-1)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Back
        </button>

        {/* ── Header: title, meta, description, play ───────────────── */}
        <div className="gdp__content">
          <p className="m-eyebrow gdp__eyebrow">Guide</p>
          <div className="gdp__title-row">
            {editing
              ? <input
                  className="gdp__title gdp__title-input"
                  value={draft.name}
                  onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))}
                  placeholder="Guide name"
                  aria-label="Guide name"
                  autoFocus
                />
              : <h1 className="gdp__title">{guide.name}</h1>}
            {guide.is_public
              ? <span className="m-badge m-badge--public">Public</span>
              : guide.shared_emails?.length > 0
                ? <span className="m-badge m-badge--shared">Shared</span>
                : <span className="m-badge m-badge--private">Private</span>
            }
          </div>

          <div className="gdp__meta">
            {editing ? (
              <label className="gdp__shortcut gdp__shortcut--edit" title="Shortcut (must be unique)">
                <span>/</span>
                <input
                  value={draft.shortcut}
                  onChange={(e) => setDraft(d => ({ ...d, shortcut: e.target.value.replace(/\s+/g, '-').toLowerCase() }))}
                  placeholder="shortcut"
                  aria-label="Shortcut"
                />
              </label>
            ) : (
            <button
              className={`gdp__shortcut ${copied ? 'gdp__shortcut--copied' : ''}`}
              onClick={handleCopyShortcut}
              title="Click to copy shortcut"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
              {copied ? 'Copied' : guide.shortcut}
            </button>
            )}
            <span className="gdp__stat m-mono">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
              </svg>
              {stepCount} {stepCount === 1 ? 'step' : 'steps'}
            </span>
            {isOwner && playCount !== null && (
              <span className="gdp__stat m-mono">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <polygon points="5 3 19 12 5 21 5 3"/>
                </svg>
                {playCount === 0 ? 'No plays' : `${playCount} ${playCount === 1 ? 'play' : 'plays'}`}
              </span>
            )}
            {startUrl && (
              <span className="gdp__stat m-mono">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
                {getDomain(startUrl)}
              </span>
            )}
          </div>

          {editing
            ? <Grow
                className="gdp__desc gdp__desc-input"
                value={draft.description}
                onChange={(e) => setDraft(d => ({ ...d, description: e.target.value }))}
                placeholder="Add a description…"
                aria-label="Description"
              />
            : guide.description && <p className="gdp__desc">{guide.description}</p>}

          {/* ── Primary: Play ─────────────────────────────────────── */}
          <div className="gdp__cta-row">
            <button className="gdp__play-btn lp-btn" onClick={() => handlePlay()} disabled={playState === 'launching'}>
              {playState === 'launching'
                ? <span className="gdp__spinner" />
                : <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                    <polygon points="5 3 19 12 5 21 5 3"/>
                  </svg>
              }
              {playState === 'launching' ? 'Opening…' : 'Play guide'}
            </button>

            {hasExtension === true && playState === 'idle' && (
              <span className="gdp__ext-note"><span className="gdp__green-dot" />Extension ready</span>
            )}
            {hasExtension === false && playState === 'idle' && (
              <span className="gdp__ext-note gdp__ext-note--muted">Needs the Michii extension</span>
            )}
          </div>

          {/* ── Play status ───────────────────────────────────────── */}
          {playState === 'needs_extension' && (
            <div className="gdp__status gdp__status--warn">
              <strong>Add the Michii extension to play this guide</strong>
              <span>We opened the Chrome Web Store in a new tab. Install it, then come back and reload this page.</span>
              <div className="gdp__status-actions">
                <a className="gdp__action-btn" href={CHROME_STORE_URL} target="_blank" rel="noreferrer"
                   onClick={() => ReactGA.event('chrome_store_click', { click_source: 'guide_detail_banner' })}>
                  Add to Chrome, free
                </a>
                <button className="gdp__action-btn" onClick={recheckExtension}>I've installed it, reload</button>
              </div>
            </div>
          )}
          {playState === 'waiting_login' && (
            <div className="gdp__status gdp__status--warn">
              <strong>Waiting for you to log in</strong>
              <span><b>{getDomain(startUrl || '')}</b> redirected you to its login page. Sign in there and the guide resumes automatically.</span>
              <div className="gdp__status-actions">
                <button className="gdp__action-btn" onClick={() => setPlayState('idle')}>Cancel</button>
              </div>
            </div>
          )}
          {playState === 'playing' && (
            <div className="gdp__status gdp__status--ok">
              <strong>Guide is running</strong>
              <span>Follow the highlighted steps in the new browser tab.</span>
              <div className="gdp__status-actions">
                <button className="gdp__action-btn" onClick={() => setPlayState('idle')}>Done</button>
              </div>
            </div>
          )}
          {playState === 'error' && (
            <div className="gdp__status gdp__status--error">
              <strong>Something went wrong</strong>
              <span>{playError}</span>
              <div className="gdp__status-actions">
                <button className="gdp__action-btn" onClick={() => handlePlay()}>Try again</button>
                <button className="gdp__action-btn" onClick={() => { setPlayState('idle'); setPlayError(''); }}>Cancel</button>
              </div>
            </div>
          )}

          {/* ── Owner actions ─────────────────────────────────────── */}
          {isOwner && (
            <div className="gdp__actions">
              <button className="gdp__action-btn" onClick={startEdit} disabled={editing}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                Edit
              </button>
              <button className="gdp__action-btn" onClick={() => setSharingGuide(guide)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
                Share
              </button>
              <button className="gdp__action-btn" onClick={handleDownloadPdf} disabled={pdfLoading}>
                {pdfLoading
                  ? <span className="gdp__spinner" />
                  : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                }
                {pdfLoading ? 'Exporting…' : 'PDF'}
              </button>
              <button
                className="gdp__action-btn"
                onClick={handleDownloadVideo}
                disabled={videoLoading}
              >
                {videoLoading
                  ? <span className="gdp__spinner" />
                  : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
                    </svg>
                }
                {videoLoading ? 'Exporting…' : 'Video'}
              </button>
              {userPlan && userPlan !== 'free' && (
                <>
                  <button className="gdp__action-btn" onClick={handleDownloadPdfPrivacy} disabled={pdfPrivacyLoading}>
                    {pdfPrivacyLoading
                      ? <span className="gdp__spinner" />
                      : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                          <line x1="9" y1="14" x2="15" y2="14"/><line x1="9" y1="18" x2="11" y2="18"/>
                        </svg>
                    }
                    {pdfPrivacyLoading ? 'Exporting…' : 'PDF (Privacy)'}
                  </button>
                  <button
                    className="gdp__action-btn"
                    onClick={handleDownloadVideoPrivacy}
                    disabled={videoPrivacyLoading}
                  >
                    {videoPrivacyLoading
                      ? <span className="gdp__spinner" />
                      : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
                          <line x1="5" y1="9" x2="11" y2="9"/>
                        </svg>
                    }
                    {videoPrivacyLoading ? 'Exporting…' : 'Video (Privacy)'}
                  </button>
                </>
              )}
              <button className="gdp__action-btn gdp__action-btn--danger" onClick={() => setConfirmDel(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
                Delete
              </button>
            </div>
          )}

          {videoError && <p className="page-error gdp__video-error">{videoError}</p>}
        </div>

        {/* ── Steps ────────────────────────────────────────────────── */}
        {viewerSteps.length > 0 && (
          <StepViewer steps={viewerSteps} token={token} editing={editing} onStepChange={editStep} />
        )}
      </div>

      {/* ── Confirm delete ──────────────────────────────────────────── */}
      {confirmDel && (
        <div className="gdp__confirm-backdrop">
          <div className="gdp__confirm m-sheet" role="alertdialog" aria-label="Delete guide">
            <p>Delete <strong>{guide.name}</strong>? This cannot be undone.</p>
            <div className="gdp__confirm-actions">
              <button className="lp-btn sm quiet" onClick={() => setConfirmDel(false)}>Keep it</button>
              <button className="gdp__confirm-delete lp-btn sm" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {sharingGuide && (
        <ShareModal
          guide={sharingGuide}
          token={token}
          onClose={() => setSharingGuide(null)}
          onUpdate={handleGuideUpdate}
        />
      )}

      {editing && (
        <div className="gdp__savebar">
          <span className="gdp__savebar-hint">
            {saveError || (dirty ? 'Unsaved changes · ⌘↵ to save' : 'Edit the title, shortcut, description or any step')}
          </span>
          <button className="gdp__action-btn" onClick={cancelEdit}>Cancel</button>
          <button
            className="gdp__save-btn lp-btn sm"
            onClick={saveEdit}
            disabled={saving || !dirty || !draft.name.trim() || !draft.shortcut.trim()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
};

export default GuideDetailPage;
